import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { SessionCard as SessionCardModel } from "../../features/messages/models/SessionCard";
import { formatOccurrence } from "../../features/messages/utils/systemLineText";

interface SessionCardProps {
  card: SessionCardModel;
  /** Where "Open" leads: the Session page for staff, the portal schedule for a member. */
  openTo: string;
}

/** The Session's facts pinned above its thread: a hairline block with a thin accent rule on the left. */
const SessionCard = ({ card, openTo }: SessionCardProps) => {
  const { t, i18n } = useTranslation();

  return (
    <div
      className="mt-3 border border-l-2 border-grey-90 border-l-primary-40 px-4 py-3 dark:border-primary-20 dark:border-l-primary-90"
      data-testid="session-card"
    >
      <div className="flex items-baseline justify-between gap-3">
        <p className="truncate font-semibold text-grey-20 dark:text-white">{card.name}</p>
        <Link to={openTo} className="shrink-0 text-sm text-primary-40 hover:underline dark:text-primary-90">
          {t("messages.card.open")}
        </Link>
      </div>
      <dl className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 text-sm">
        <dt className="text-grey-40 dark:text-grey-80">{t("messages.card.next")}</dt>
        <dd className="text-grey-20 dark:text-white" data-testid="session-card-next">
          {card.isCancelled
            ? t("messages.card.cancelled")
            : card.nextOccurrence
              ? formatOccurrence(card.nextOccurrence, i18n.language)
              : t("messages.card.noNext")}
        </dd>
        {card.place && (
          <>
            <dt className="text-grey-40 dark:text-grey-80">{t("messages.card.place")}</dt>
            <dd className="text-grey-20 dark:text-white">{card.place}</dd>
          </>
        )}
      </dl>
      <p className="mt-1 text-sm text-grey-40 dark:text-grey-80">
        {t("messages.card.roster", { count: card.rosterCount })}
      </p>
    </div>
  );
};

export default SessionCard;
